import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  PagesActionTypes,
  LoadPages,
  LoadPagesSuccess,
  LoadPagesFailed,
  AddPage,
  AddPageSuccess,
  AddPageFailed,
  DeletePage,
  DeletePageSuccess,
  DeletePageFailed,
  UpdatePage,
  UpdatePageSuccess,
  UpdatePageFailed,
  LoadPage,
  LoadPageSuccess,
  LoadPageFailed,
  LoadDefaultPageForTable,
  LoadDefaultPageForTableSuccess,
  LoadDefaultPageForTableFailed,
  CopyPage,
  CopyPageSuccess,
  CopyPageFailed,
} from '../../_actions/settings/pages.action';
import { CacheService } from '../../../services/cache.service';
import { AvailableCaches } from '../../_models/ignatius.models';
import { SettingsService } from '../../_services/settings.service';
import {
  getAllPages,
  getDefaultPageForTable,
  getPageDetail,
} from '../../_selectors/settings/pages.selector';
import { AddPageResponse, CopyPageResponse } from '../../_models/page.model';
import {
  AlertService,
  ToastTypes,
} from 'src/app/shared/services/alert.service';
import { Pages } from '../../_models/settings.models';

@Injectable()
export class PagesEffects {
  constructor(
    private actions: Actions,
    private service: SettingsService,
    private store: Store,
    private cache: CacheService,
    private alert: AlertService
  ) {}

  /**
   * Get Pages
   */
  loadPages: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadPages>(PagesActionTypes.LOAD_PAGES),
      withLatestFrom(this.store.pipe(select(getAllPages))),
      mergeMap(([action, pages]) => {
        if (
          pages[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.PAGES)
        )
          return of(
            new LoadPagesSuccess(
              action.applicationId,
              pages[action.applicationId]
            )
          );
        return this.service.getPages(action.applicationId).pipe(
          map(
            (payload: Array<any>) =>
              new LoadPagesSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadPagesFailed(err, action.applicationId)))
        );
      })
    )
  );

  /**
   * Load a Page
   */
  loadPage: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadPage>(PagesActionTypes.LOAD_PAGE),
      withLatestFrom(this.store.pipe(select(getPageDetail))),
      mergeMap(([action, pages]) => {
        if (
          pages[action.appId] &&
          pages[action.appId][action.pageId] &&
          this.cache.getCacheState(AvailableCaches.PAGES)
        )
          return of(
            new LoadPageSuccess(
              action.appId,
              action.pageId,
              pages[action.appId][action.pageId]
            )
          );
        return this.service.getPage(action.pageId).pipe(
          map(
            (payload: Array<any>) =>
              new LoadPageSuccess(action.appId, action.pageId, payload)
          ),
          catchError((err) => of(new LoadPageFailed(err)))
        );
      })
    )
  );
  /**
   * Load Default Page for table
   */
  loadDefaultPageForTable: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadDefaultPageForTable>(
        PagesActionTypes.LOAD_DEFAULT_PAGE_FOR_TABLE
      ),
      withLatestFrom(this.store.pipe(select(getDefaultPageForTable))),
      mergeMap(([action, pages]) => {
        // if (
        //   pages[action.TableId] &&
        //   pages[action.TableId][action.TableId] &&
        //   this.cache.getCacheState(AvailableCaches.PAGES)
        // )
        //   return of(
        //     new LoadPageSuccess(
        //       action.appId,
        //       action.pageId,
        //       pages[action.appId][action.pageId]
        //     )
        //   );
        return this.service.getDefaultPageByTableId(action.TableId).pipe(
          map((payload: Array<any>) => {
            return new LoadDefaultPageForTableSuccess(action.TableId, payload);
          }),
          catchError((err) => of(new LoadDefaultPageForTableFailed(err)))
        );
      })
    )
  );

  /**
   * Add a Page
   */
  addPage: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<AddPage>(PagesActionTypes.ADD_PAGE),
      mergeMap((action) => {
        return this.service.addPage(action.payload).pipe(
          map((payload: AddPageResponse) => {
            const instance = new AddPageSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new AddPageFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Update a Page
   */
  updatePage: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdatePage>(PagesActionTypes.UPDATE_PAGE),
      mergeMap((action) => {
        return this.service.updatePage(action.payload).pipe(
          map((payload: Pages) => {
            const instance = new UpdatePageSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdatePageFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Delete a Page
   */
  deletePage: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<DeletePage>(PagesActionTypes.DELETE_PAGE),
      mergeMap((action) => {
        return this.service.deletePage(action.pageId).pipe(
          map(() => {
            const instance = new DeletePageSuccess(action.pageId, action.appId);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new DeletePageFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Copy Page
   */
   copyPage: Observable<Action> = createEffect(() =>
   this.actions.pipe(
     ofType<CopyPage>(PagesActionTypes.COPY_PAGE),
     mergeMap((action) => {
       return this.service.copyPage(action.payload).pipe(
         map((payload: CopyPageResponse) => {
           const instance = new CopyPageSuccess(payload);
           instance.refId = action.refId;
           this.store.dispatch(
             new LoadPages(action.appId, false)
           );
           return instance;
         }),
         catchError((err) => {
           const instance = new CopyPageFailed(err);
           instance.refId = action.refId;
           return of(instance);
         })
       );
     })
   )
 );
}
